
import React, { useEffect, useState } from 'react';
import {
    Box, Container, Typography, Chip, Grid, Card, CardMedia, CardContent,
    Button, Stack, Dialog, DialogContent, IconButton, DialogTitle,
    Skeleton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import GujaratiSamajImg from '../assets/Images/gujarati_samaj.png';
import API from "../config/axios";
import { api_paths } from '../config/api.paths';
import SharePopover from './SharePopover';
import { Link } from 'react-router-dom';
import { blue, lightBlue } from '@mui/material/colors';

const PortfolioSection = ({data}) => {
    const IMG_PATH = process.env.REACT_APP_IMAGE_BASEURL || "";
    const { t } = useTranslation();
    const theme = useTheme();
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [open, setOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [filters, setFilters] = useState([]);
    const [projects, setProjects] = useState([]);

    const isLoading = data === undefined;

    const handleOpen = (project) => {
        setSelectedProject(project);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedProject(null);
    };

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await API.get(api_paths.CATEGORY.get);
                // console.log("Category List:", response.data);

                const categoryList = response.data?.payload?.data?.categories || [];

                setFilters([
                    { _id: 'all', name: t('pages.home.portfolio.filters.all') },
                    ...categoryList
                ]);
                // console.log("actual data", categoryList);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        const fetchProjects = async () => {
            try {
                const response = await API.get(api_paths.PROJECT.get);
                // console.log("Project List:", response.data);

                const projectList = response.data?.payload?.data?.projects || [];
                setProjects(projectList);
                // console.log("project data", projectList);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        }

        fetchCategories();
        fetchProjects();

    }, []);

    const filteredProjects = selectedFilter === 'all'
        ? projects
        : projects.filter(project => project?.category?._id === selectedFilter);

    return (
        <Box id="portfolio" sx={{ py: 10, backgroundColor: theme.palette.background.default }}>
            <Container maxWidth="xl">
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Chip
                        label={t('pages.home.portfolio.chip_title')}
                        sx={{
                            backgroundColor: theme.palette.background.chip,
                            color: theme.palette.custom.chipText,
                            fontWeight: 300,
                            fontSize: '1rem',
                            mb: 2,
                            px: 2
                        }}
                    />
                    <Typography variant="h3" component="h2" fontWeight="bold" sx={{
                        color: theme.typography.h3.color || '#3784b4ff',
                        textAlign: 'center',
                        width: { xs: '100%', sm: '80%', md: '60%', lg: '40%' },
                        margin: '0 auto',
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem', lg: '3.5rem' }
                    }}>
                        {isLoading ? (
                            <Skeleton variant="text" height={60} width="80%" />
                        ) : (
                            data?.meta?.title || t('pages.home.portfolio.headline')
                        )}
                    </Typography>
                    <Typography variant="h6" fontWeight="light" color={theme.palette.text.secondary} sx={{ mb: 4, mt: 4, px: { xs: 2, md: 0 } }}>
                        {isLoading ? (
                            <Skeleton variant="text" height={30} width="100%" />
                        ) : (
                            data?.meta?.description || t('pages.home.portfolio.subheadline')
                        )}
                    </Typography>

                    <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mb: 6 }}>
                        {filters.map((filter) => (
                            <Button
                                key={filter._id}
                                onClick={() => setSelectedFilter(filter._id)}
                                variant={selectedFilter === filter._id ? 'contained' : 'text'}
                                sx={{
                                    borderRadius: '20px',
                                    textTransform: 'none',
                                    px: 2,          // 👈 FIX horizontal padding
                                    py: 1,            // 👈 FIX vertical padding
                                    minWidth: 'auto', // optional, avoids width jumps
                                    backgroundColor:
                                        selectedFilter === filter._id
                                            ? theme.palette.primary.main
                                            : theme.palette.background.section,

                                    color:
                                        selectedFilter === filter._id
                                            ? theme.palette.primary.contrastText
                                            : theme.palette.text.secondary,

                                    boxShadow:
                                        selectedFilter === filter._id
                                            ? theme.palette.custom.activeBlueShadow
                                            : 'none',
                                    mb: 1,
                                    '&:hover': {
                                        backgroundColor: theme.palette.primary.main,
                                        color: theme.palette.primary.contrastText,
                                    },
                                }}
                            >
                                {filter.name}
                            </Button>

                        ))}
                    </Stack>
                </Box>

                
                {filteredProjects.length > 0 ? (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                md: 'repeat(3, 1fr)',
                            },
                            gap: 4,
                        }}
                    >
                    {
                        filteredProjects.map((project) => (
                            <Card
                                key={project._id}
                                sx={{
                                    height: '100%',
                                    borderRadius: '16px',
                                boxShadow: theme.palette.custom.cardShadow,
                                transition: 'all 0.3s ease-in-out',
                                overflow: 'hidden',
                                '&:hover': {
                                    transform: 'translateY(-6px)',
                                    boxShadow: theme.palette.custom.cardShadowHover,
                                    '& .hover-overlay': {
                                        opacity: 1,
                                    },
                                },
                            }}
                            >
                                <Box sx={{ position: 'relative' }}>
                                    <CardMedia
                                        component="img"
                                        image={project?.image ? IMG_PATH + project.image : "/placeholder.png"}
                                        alt={project?.title}
                                        sx={{
                                            height: { xs: 180, sm: 200, md: 300 },
                                            objectFit: 'cover',
                                        }}
                                    />

                                    <Box
                                        className="hover-overlay"
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            backgroundColor: theme.palette.custom.overlay,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            opacity: 0,
                                            transition: 'opacity 0.3s ease',
                                        }}
                                    >
                                        <Button
                                            variant="contained"
                                            endIcon={<ArrowForwardIcon />}
                                            onClick={() => handleOpen(project)}
                                            sx={{
                                                backgroundColor: theme.palette.common.white,
                                                color: theme.palette.text.link,
                                                fontWeight: 'bold',
                                                textTransform: 'none',
                                                borderRadius: '20px',
                                                px: 2,
                                                '&:hover': {
                                                    backgroundColor: theme.palette.grey[100],
                                                }
                                            }} >
                                            {t('pages.home.portfolio.view_details')}
                                        </Button>
                                    </Box>
                                </Box>

                                <CardContent sx={{ p: 4 }}>
                                    <Typography variant="overline" color={blue[500]} fontWeight="500">
                                        {project.category?.name?.toUpperCase()}
                                    </Typography>
                                    <Typography variant="h6" component="h3" fontWeight="medium" sx={{ mt: 1, mb: 1 }}>
                                        {project?.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        {project?.description}
                                    </Typography>
                                    <Stack direction="row" spacing={1}>
                                        {project?.technology.map((tag, index) => (
                                            <Chip
                                                key={`${tag}-${index}`}
                                                label={tag}
                                                size="small"
                                                sx={{
                                                    backgroundColor: theme.palette.custom.tagBg,
                                                    color: theme.palette.custom.tagText,
                                                    fontWeight: 400
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                </CardContent>
                            </Card>
                        
                        ))
                    }
                    </Box>
                ) : (
                    <Box>
                        <Typography variant="h6" color="text.secondary" textAlign="center">
                            {t('pages.home.portfolio.no_projects')}
                        </Typography>
                    </Box>
                    )
                }
                


                <Box sx={{ textAlign: 'center', mt: 8 }}>
                    <Button
                        variant="outlined"
                        endIcon={<OpenInNewIcon />}
                        sx={{
                            borderColor: theme.palette.custom.viewAllBtnBorder,
                            color: theme.palette.custom.viewAllBtnText,
                            backgroundColor: theme.palette.custom.viewAllBtnBg,
                            px: 3,
                            py: 1,
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontSize: '1rem',
                            fontWeight: 400,
                            borderWidth: '1px',
                            '&:hover': {
                                backgroundColor: theme.palette.custom.viewAllBtnHoverBg,
                                color: theme.palette.custom.viewAllBtnHoverText,
                                borderColor: theme.palette.custom.viewAllBtnHoverBg,
                            }
                        }}
                    >
                        {t('pages.home.portfolio.view_all_projects')}
                    </Button>
                </Box>
            </Container>

            {/* Project Details Modal */}
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        overflow: 'hidden'
                    }
                }}
            >
                {selectedProject && (
                    <>
                        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h5" component="div" fontWeight="bold">
                                {selectedProject?.title}
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SharePopover
                                    url={window.location.href}
                                    title={selectedProject?.title}
                                />

                                <IconButton
                                    aria-label="close"
                                    onClick={handleClose}
                                    sx={{
                                        color: (theme) => theme.palette.grey[500],
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent dividers sx={{ p: 4 }}>
                            <CardMedia
                                component="img"
                                image={selectedProject?.image ? IMG_PATH + selectedProject.image : "/placeholder.png"}
                                alt={selectedProject?.title}
                                sx={{
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: '400px',
                                    objectFit: 'cover',
                                    borderRadius: '12px',
                                    mb: 4
                                }}
                            />

                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Box sx={{ backgroundColor: theme.palette.background.section, p: 2, borderRadius: '8px' }}>
                                        <Typography variant="body2" color="text.secondary" fontWeight="bold">
                                            {t('pages.home.portfolio.modal.client')}
                                        </Typography>
                                        <Typography variant="h6" fontWeight="bold">
                                            {selectedProject?.client}
                                        </Typography>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Box sx={{ backgroundColor: theme.palette.background.section, p: 2, borderRadius: '8px' }}>
                                        <Typography variant="body2" color="text.secondary" fontWeight="bold">
                                            {t('pages.home.portfolio.modal.duration')}
                                        </Typography>
                                        <Typography variant="h6" fontWeight="bold">
                                            {selectedProject?.duration}
                                        </Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                                    {t('pages.home.portfolio.modal.about')}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    {selectedProject?.description}
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                                    {t('pages.home.portfolio.modal.technologies')}
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {selectedProject?.technology.map((tag, index) => (
                                        <Chip
                                            key={`${tag}-${index}`}
                                            label={tag}
                                            sx={{
                                                backgroundColor: theme.palette.custom.tagBg,
                                                color: theme.palette.custom.tagText,
                                                fontWeight: 500,
                                                // mb: 1
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Box>

                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                sx={{ mt: 3 }}
                            >
                                {/* View Full Details Button */}
                                <Button
                                    component={Link}
                                    to={`/portfolio/${selectedProject._id}`}
                                    variant="outlined"
                                    fullWidth
                                    size="large"
                                    endIcon={<OpenInNewIcon />}
                                    sx={{
                                    borderRadius: "8px",
                                    fontWeight: "bold",
                                    textTransform: "none",
                                    fontSize: { xs: "0.85rem", sm: "1rem" },
                                    py: { xs: 1, sm: 1.5 },
                                    }}
                                >
                                    View Full Details
                                </Button>

                                {/* Start Similar Project Button */}
                                <Button
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{
                                    backgroundImage: theme.palette.custom.buttonGradient,
                                    borderRadius: "8px",
                                    fontWeight: "bold",
                                    textTransform: "none",
                                    fontSize: { xs: "0.85rem", sm: "1rem" },
                                    py: { xs: 1, sm: 1.5 },
                                    "&:hover": {
                                        backgroundImage: theme.palette.custom.buttonGradient,
                                        opacity: 0.9,
                                        boxShadow: "none",
                                    },
                                    }}
                                >
                                    {t("pages.home.portfolio.modal.start_similar")}
                                </Button>
                            </Stack>
                        </DialogContent>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default PortfolioSection;

